import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { shopAPI } from '../../../services/api';

// Async thunk для загрузки товаров магазина
export const fetchShopItems = createAsyncThunk(
  'shop/fetchItems',
  async (type = null, { rejectWithValue }) => {
    try {
      const items = await shopAPI.getGoods(type);
      return { type, items };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk для загрузки всех типов товаров (один запрос, группировка по type на клиенте)
export const fetchAllShopItems = createAsyncThunk(
  'shop/fetchAllItems',
  async (_, { rejectWithValue }) => {
    try {
      return await shopAPI.getGoods(null);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk для загрузки бустов
export const fetchShopBoosts = createAsyncThunk(
  'shop/fetchBoosts',
  async (_, { rejectWithValue }) => {
    try {
      const boosts = await shopAPI.getBoosts();
      return boosts;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk для загрузки пакетов AutoCollect
export const fetchAutoCollectItems = createAsyncThunk(
  'shop/fetchAutoCollectItems',
  async (_, { rejectWithValue }) => {
    try {
      const items = await shopAPI.getAutoCollectItems();
      return items;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk для загрузки паков
export const fetchShopPacks = createAsyncThunk(
  'shop/fetchPacks',
  async (_, { rejectWithValue }) => {
    try {
      const packs = await shopAPI.getPacks();
      return packs;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const initialState = {
  items: {
    COINS: [],
    TICKETS: [],
    KEY: [],
    CHEST: [],
  },
  packs: [],
  boosts: [],
  autoCollectItems: [],
  isLoading: false,
  isLoadingPacks: false,
  error: null,
};

const shopSlice = createSlice({
  name: 'shop',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setLoading: (state, action) => {
      state.isLoading = action.payload;
    },
    // Обновление товаров через WebSocket (если понадобится)
    updateFromWebSocket: (state, action) => {
      const { type, items } = action.payload;
      if (type && items) {
        state.items[type] = items;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // fetchShopItems
      .addCase(fetchShopItems.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchShopItems.fulfilled, (state, action) => {
        state.isLoading = false;
        const { type, items } = action.payload;
        if (type) {
          state.items[type] = items;
        }
      })
      .addCase(fetchShopItems.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // fetchAllShopItems
      .addCase(fetchAllShopItems.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchAllShopItems.fulfilled, (state, action) => {
        state.isLoading = false;
        // Reset known buckets, then fill from the flat response grouped by item.type
        const byType = { COINS: [], TICKETS: [], KEY: [], CHEST: [], LIVES: [] };
        for (const item of action.payload) {
          if (byType[item.type] !== undefined) {
            byType[item.type].push(item);
          } else {
            byType[item.type] = [item];
          }
        }
        state.items = byType;
      })
      .addCase(fetchAllShopItems.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // fetchShopBoosts
      .addCase(fetchShopBoosts.fulfilled, (state, action) => {
        state.boosts = action.payload;
      })
      // fetchAutoCollectItems
      .addCase(fetchAutoCollectItems.fulfilled, (state, action) => {
        state.autoCollectItems = action.payload;
      })
      // fetchShopPacks
      .addCase(fetchShopPacks.pending, (state) => {
        state.isLoadingPacks = true;
        state.error = null;
      })
      .addCase(fetchShopPacks.fulfilled, (state, action) => {
        state.isLoadingPacks = false;
        state.packs = action.payload;
      })
      .addCase(fetchShopPacks.rejected, (state, action) => {
        state.isLoadingPacks = false;
        state.error = action.payload;
      });
  },
});

export const { clearError, setLoading, updateFromWebSocket } = shopSlice.actions;
export default shopSlice.reducer;

